import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { authApi } from "@/api/auth";
import { getSession, setSession, clearSession, subscribe, getToken, type StoredSession } from "@/lib/session";
import type { AuthResponse } from "@/types/api";

interface AuthContextValue {
  session: StoredSession | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  ready: boolean;
  login: (userName: string, password: string) => Promise<void>;
  register: (email: string, fullName: string, password: string) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function sessionFromAuth(res: AuthResponse): StoredSession {
  return {
    token: res.token,
    expiresAtUtc: res.expiresAtUtc,
    refreshToken: res.refreshToken ?? null,
    userId: res.userId,
    fullName: res.fullName,
    userName: res.userName,
  };
}

async function enrichWithMe(): Promise<void> {
  try {
    const me = await authApi.me();
    const s = getSession();
    if (s) setSession({ ...s, isAdmin: me.isAdmin, fullName: me.fullName || s.fullName, userName: me.userName || s.userName });
  } catch {
    /* 401 esetén az interceptor már kiléptetett */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const session = useSyncExternalStore(subscribe, getSession, getSession);
  const [ready, setReady] = useState(false);

  // Kezdeti hidratálás: ha van token, ellenőrizzük /me-vel (és feltöltjük az isAdmin-t).
  useEffect(() => {
    let alive = true;
    (async () => {
      if (getToken()) await enrichWithMe();
      if (alive) setReady(true);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const login = useCallback(async (userName: string, password: string) => {
    const res = await authApi.login({ userName, password });
    setSession(sessionFromAuth(res));
    await enrichWithMe();
  }, []);

  const register = useCallback(async (email: string, fullName: string, password: string) => {
    const res = await authApi.register({ email, fullName, password });
    setSession(sessionFromAuth(res));
    await enrichWithMe();
  }, []);

  const loginWithGoogle = useCallback(async (idToken: string) => {
    const res = await authApi.google({ idToken });
    setSession(sessionFromAuth(res));
    await enrichWithMe();
  }, []);

  const logout = useCallback(() => {
    clearSession();
  }, []);

  const value: AuthContextValue = {
    session,
    isAuthenticated: !!session,
    isAdmin: !!session?.isAdmin,
    ready,
    login,
    register,
    loginWithGoogle,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth csak <AuthProvider>-en belül használható.");
  return ctx;
}
